import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { Pill } from "../components/Pill";

const RULE_NAME = "Tracked Client & Matter Names";
const OLD_RULE_NAME = "Tracked client & matter names"; // legacy

interface TrackedTerm {
  term: string;
  mapping: string;
}

function defaultMapping(term: string): string {
  if (/^project\b/i.test(term)) return "the project";
  if (/\b(ltd|limited|llc|llp|inc|plc|pty|gmbh|co)\b/i.test(term)) return "the organisation";
  return "the client";
}

function parseTerms(rule: any): TrackedTerm[] {
  if (!rule?.patterns) return [];
  const mappings: Record<string, string> = rule.metadata?.mappings ?? {};
  return rule.patterns.map((p: any) => {
    // Pattern value is a regex — pull the term out of the `metadata.terms` if present,
    // otherwise fall back to the raw value.
    const term = p.term ?? p.value.replace(/\\b|\(\?:'s\)\?|\\s\+/g, " ").replace(/\\/g, "").trim();
    return { term, mapping: mappings[term] ?? defaultMapping(term) };
  });
}

export function TrackedNamesPage() {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [action, setAction] = useState<"block" | "suggest_rewrite" | "warn">("block");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    const res = await api.getPolicyBundle();
    setRules(res.rules);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const rule = useMemo(
    () => rules.find((r) => r.name === RULE_NAME) ?? rules.find((r) => r.name === OLD_RULE_NAME),
    [rules]
  );
  const terms: TrackedTerm[] = useMemo(() => parseTerms(rule), [rule]);

  async function saveTerms(nextTerms: TrackedTerm[], nextAction = action) {
    setBusy(true);
    try {
      const mappings: Record<string, string> = {};
      const patterns = nextTerms.map(({ term, mapping }) => {
        mappings[term] = mapping;
        const escape = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const tokenised = escape.split(/\s+/).filter(Boolean).join("\\s+");
        return {
          kind: "regex",
          value: `\\b${tokenised}(?:'s)?\\b`,
          weight: 18,
          caseInsensitive: true,
          term,            // we store the original term for round-tripping
        } as any;
      });

      const base = {
        name: RULE_NAME,
        category: "legal_client_matter",
        description: "Workspace-specific names and identifiers. Matches are case-insensitive and whole-word.",
        severity: "high",
        action: nextAction,
        enabled: nextTerms.length > 0,
        scope: "workspace",
        domains: [],
        teams: [],
        patterns,
        threshold: 1,
        rewriteMode: "suggest",
        allowUserOverride: false,
        logMode: "masked",
        metadata: { mappings },
      };

      if (rule) {
        await api.updateRule({ rule: { ...rule, ...base } });
      } else if (nextTerms.length > 0) {
        await api.createRule({ rule: base });
      }
      await load();
    } finally { setBusy(false); }
  }

  async function addTerm() {
    const v = input.trim();
    if (!v) return;
    if (terms.some((t) => t.term === v)) { setInput(""); return; }
    await saveTerms([...terms, { term: v, mapping: defaultMapping(v) }]);
    setInput("");
  }

  async function removeTerm(term: string) {
    await saveTerms(terms.filter((t) => t.term !== term));
  }

  async function updateMapping(term: string, mapping: string) {
    await saveTerms(terms.map((t) => (t.term === term ? { ...t, mapping } : t)));
  }

  async function changeAction(a: typeof action) {
    setAction(a);
    if (rule) await saveTerms(terms, a);
  }

  return (
    <>
      <header className="sg-page-header">
        <h1 className="sg-page-title">Tracked Names</h1>
        <div className="sg-page-sub">Workspace-Specific · Client / Matter / Project Identifiers</div>
      </header>

      <div className="sg-install-strip" style={{ background: "var(--sg-bg-1)", borderLeftColor: "var(--sg-blue)" }}>
        <div style={{ flex: 1 }}>
          <div className="sg-install-title">Flag Sensitive Identifiers by Name</div>
          <div className="sg-muted" style={{ fontSize: 12.5 }}>
            Add client names, matter codes, internal project names, and counterparty identifiers. Any prompt containing these terms will be evaluated at the severity you choose, and replaced by the mapping shown below when the prompt is auto-anonymised.
          </div>
        </div>
      </div>

      <div className="sg-section-label">Add Term</div>
      <div className="sg-panel" style={{ marginBottom: 30 }}>
        <div className="sg-panel-pad">
          <div className="sg-row" style={{ gap: 10, flexWrap: "wrap" }}>
            <input
              className="sg-input"
              placeholder="e.g. Acme Corp · Matter 2024-1142 · Project Cascade"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addTerm(); }}
              style={{ flex: 1, minWidth: 280 }}
              disabled={busy}
            />
            <select
              className="sg-select"
              value={action}
              onChange={(e) => changeAction(e.target.value as any)}
              disabled={busy}
            >
              <option value="block">Block</option>
              <option value="suggest_rewrite">Suggest Rewrite</option>
              <option value="warn">Warn</option>
            </select>
            <button className="sg-btn sg-btn-primary" onClick={addTerm} disabled={busy || !input.trim()}>
              Add Term
            </button>
          </div>
          <div className="sg-muted" style={{ fontSize: 12, marginTop: 10 }}>
            Matches are case-insensitive and whole-word. Possessives like <code>Cascade's</code> are detected automatically.
          </div>
        </div>
      </div>

      <div className="sg-section-label">
        Tracked Terms {terms.length > 0 && <span style={{ color: "var(--sg-ink-4)", fontWeight: 500 }}>({terms.length})</span>}
      </div>

      {loading ? (
        <div className="sg-empty">Loading…</div>
      ) : terms.length === 0 ? (
        <div className="sg-empty">
          <strong>No Tracked Terms Yet</strong>
          Add client names, matter numbers, or internal project names above.
        </div>
      ) : (
        <div className="sg-panel">
          <table className="sg-table">
            <thead>
              <tr>
                <th style={{ width: "38%" }}>Term</th>
                <th style={{ width: "38%" }}>Maps To (When Anonymised)</th>
                <th>Action on Match</th>
                <th style={{ textAlign: "right" }}>Manage</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((t) => (
                <tr key={t.term}>
                  <td style={{ fontFamily: "var(--sg-font-mono)", fontSize: 12.5 }}>{t.term}</td>
                  <td>
                    <input
                      className="sg-input"
                      value={t.mapping}
                      disabled={busy}
                      onChange={(e) => {
                        // Optimistic local update; persist on blur/enter.
                        setRules((rs) => rs.map((r) => {
                          if (r !== rule) return r;
                          const nextMappings = { ...(r.metadata?.mappings ?? {}), [t.term]: e.target.value };
                          return { ...r, metadata: { ...r.metadata, mappings: nextMappings } };
                        }));
                      }}
                      onBlur={(e) => updateMapping(t.term, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur(); }}
                      style={{ padding: "4px 8px", fontSize: 12 }}
                    />
                  </td>
                  <td><Pill kind={rule?.action === "block" ? "block" : rule?.action === "warn" ? "warn" : "rewrite"}>
                    {rule?.action === "suggest_rewrite" ? "Rewrite" : rule?.action === "warn" ? "Warn" : "Block"}
                  </Pill></td>
                  <td style={{ textAlign: "right" }}>
                    <button className="sg-btn sg-btn-sm" onClick={() => removeTerm(t.term)} disabled={busy}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
